import { jsx as _jsx } from "react/jsx-runtime";
import { addChain as walletAddChain, switchChain as walletSwitchChain, switchChainAndAddToken, useLiFiWalletManagement, } from '@lifi/wallet-management';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useWidgetConfig } from '../WidgetProvider';
const stub = () => {
    throw new Error(`You forgot to wrap your component in <${WalletProvider.name}>.`);
};
const initialContext = {
    connect: stub,
    disconnect: stub,
    switchChain: stub,
    addChain: stub,
    addToken: stub,
    account: {},
};
const WalletContext = createContext(initialContext);
export const useWallet = () => useContext(WalletContext);
export const WalletProvider = ({ children }) => {
    const { walletManagement } = useWidgetConfig();
    const { connect: walletManagementConnect, disconnect: walletManagementDisconnect, signer, provider, } = useLiFiWalletManagement();
    const [account, setAccount] = useState({});
    const connect = useCallback(async (wallet) => {
        if (walletManagement) {
            const signer = await walletManagement.connect();
            const account = await extractAccountFromSigner(signer);
            setAccount(account);
            return;
        }
        await walletManagementConnect(wallet);
    }, [walletManagement, walletManagementConnect]);
    const disconnect = useCallback(async () => {
        if (walletManagement) {
            await walletManagement.disconnect();
            setAccount({});
            return;
        }
        await walletManagementDisconnect();
    }, [walletManagement, walletManagementDisconnect]);
    // only for injected wallets
    const switchChain = useCallback(async (chainId) => {
        if (walletManagement?.switchChain) {
            const signer = await walletManagement.switchChain(chainId);
            const account = await extractAccountFromSigner(signer);
            setAccount(account);
            return true;
        }
        return walletSwitchChain(chainId);
    }, [walletManagement]);
    const addChain = useCallback(async (chainId) => {
        if (walletManagement?.addChain) {
            return walletManagement.addChain(chainId);
        }
        return walletAddChain(chainId);
    }, [walletManagement]);
    const addToken = useCallback(async (chainId, token) => {
        if (walletManagement?.addToken) {
            return walletManagement.addToken(token, chainId);
        }
        return switchChainAndAddToken(chainId, token);
    }, [walletManagement]);
    // keep account information up to date
    useEffect(() => {
        const updateAccount = async () => {
            let account;
            if (walletManagement) {
                account = await extractAccountFromSigner(walletManagement?.signer);
            }
            else {
                account = await extractAccountFromSigner(signer);
            }
            setAccount(account);
        };
        updateAccount();
    }, [signer, walletManagement]);
    const value = useMemo(() => ({
        connect,
        disconnect,
        switchChain,
        addChain,
        addToken,
        account,
        provider,
    }), [account, addChain, addToken, connect, disconnect, provider, switchChain]);
    return (_jsx(WalletContext.Provider, { value: value, children: children }));
};
export const extractAccountFromSigner = async (signer) => {
    try {
        return {
            address: await signer?.getAddress(),
            isActive: (signer && !!(await signer.getAddress()) === null) || !!signer,
            signer,
            chainId: await signer?.getChainId(),
        };
    }
    catch (error) {
        console.log(error);
        return {};
    }
};
